import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import {
  Grid,
  MainNav,
  SecondaryNav,
  TertiaryNavbar,
  SideNav,
  AdminSnackbar,
  FullScreenSpinner
} from '@custom-components';
import { withTheme } from 'styled-components';
import { TypeConsumer } from '@themes';
import { flowRight as compose } from 'lodash';
import MainNavContainer from './MainNavContainer';
import PageContainer from './PageContainer';
import { Layout, LayoutNav, LayoutPageContent, MainContainer, StyledPageWrapper } from './styles';
import withAuth, { withAuthPropType } from '../../hocs/withAuth';

function DefaultLayout(props) {
  const { children, auth } = props;
  const { authenticated = false, me = {}, reload = () => {} } = auth || {};
  const { routeName } = children.props;
  const { adminToken, activeOrganization } = me || {};
  const { tokenId, organization } = adminToken || {};
  const { name: orgName = '' } = organization || {};
  const { supplier, customer } = activeOrganization ?? {};
  const { id: supplierId } = supplier ?? {};
  const { id: customerId } = customer ?? {};

  if (!authenticated) return <FullScreenSpinner />;

  const [layoutHidden, setLayoutHidden] = useState(true);
  const [sideExpanded, setSideExpanded] = useState();
  const [tabValue, setTabValue] = useState(0);

  const prevRouteNameRef = useRef();
  const prevAuthenticatedRef = useRef();

  // Reveals the contents of the page once the layout has been mounted
  useEffect(() => {
    setSideExpanded(localStorage.getItem('isSideExpanded')?.toLowerCase() === 'true');
    setLayoutHidden(false);
  }, []);

  useEffect(() => {
    const prevRouteName = prevRouteNameRef.current;
    const prevAuthenticated = prevAuthenticatedRef.current;
    if (prevRouteName !== routeName && tabValue !== 0) {
      setTabValue(0);
    }
    if (!auth.authenticated && prevAuthenticated) {
      auth.reload();
    }
    return () => {
      prevRouteNameRef.current = routeName;
      prevAuthenticatedRef.current = authenticated;
    };
  }, [props]);

  const switchExpanded = (expanded) => {
    setSideExpanded(expanded);
    localStorage.setItem('isSideExpanded', expanded);
  };

  const renderNav = (id) => (
    <MainNavContainer type={id}>
      {({ mainNav }) => (
        <LayoutNav>
          <MainNav.MainNav
            type={id}
            isAdminToken={auth.me.adminToken !== null && id === 'supplier'}
            logo={mainNav.logo}
            helpSupport={mainNav.helpSupport}
            contactNumber="1-844-590-5465"
            authenticated={mainNav.authenticated}
            onContactClick={mainNav.onContactClick}
            userFirstName={mainNav.userFirstName}
            userLastName={mainNav.userLastName}
            userProfilePicture={mainNav.profilePicture}
            userCurrentOrganization={mainNav.userCurrentOrganization}
            organizations={mainNav.organizations}
            organziationChange={mainNav.organziationChange}
            joinOrganization={mainNav.joinLink}
            signOut={mainNav.signOut}
            subMenuLinks={mainNav.subMenuLinks}
            userMenuLinks={mainNav.userMenuLinks}
            notifications={mainNav.notifications}
            sideExpanded={sideExpanded}
            expandCollapseNav={switchExpanded}
            routeName={children.props.routeName}
          />
        </LayoutNav>
      )}
    </MainNavContainer>
  );

  const renderPrimaryNav = (id) => (
    <MainNavContainer type={id}>
      {({ mainNav }) => (
        <SideNav
          sideExpanded={sideExpanded}
          menuItems={mainNav.subMenuLinks}
          routeName={routeName}
          helpSupport={mainNav.helpSupport}
        />
      )}
    </MainNavContainer>
  );

  return (
    <TypeConsumer>
      {(id) => (
        <Layout hidden={layoutHidden}>
          {!authenticated && (
            <>{React.cloneElement(children, { tabValue, tabChange: (value) => setTabValue(value) })}</>
          )}
          {authenticated && (
            <>
              {renderNav(id)}
              <LayoutPageContent>
                <MainContainer>
                  <Grid.Visible lg xl>
                    {renderPrimaryNav(id)}
                  </Grid.Visible>
                  <PageContainer sideExpanded={sideExpanded}>
                    <SecondaryNav routeName={routeName} />
                    <TertiaryNavbar routeName={routeName} />
                    <StyledPageWrapper>
                      {React.cloneElement(children, { tabValue, tabChange: (value) => setTabValue(value) })}
                    </StyledPageWrapper>
                  </PageContainer>
                </MainContainer>
              </LayoutPageContent>
              <AdminSnackbar
                type={id}
                tokenId={tokenId}
                orgName={orgName}
                reload={reload}
                open={!!adminToken}
                supplierId={supplierId}
                customerId={customerId}
              />
            </>
          )}
        </Layout>
      )}
    </TypeConsumer>
  );
}

DefaultLayout.propTypes = {
  children: PropTypes.node,
  auth: withAuthPropType.isRequired
};

DefaultLayout.defaultProps = {
  children: null
};
export default compose(withRouter, withTheme, withAuth({ requireAuthentication: true }))(DefaultLayout);
